import { useEffect, useState } from "react";
import useUser from "./useUser";
import { useNavigate, useParams } from "react-router-dom";
import { Price, Subscription, SubscriptionRule } from "billing/billingInterface";
import { stripeApiTest } from "api";
import useTranslations from "./useTranslations";
import { getTranslation } from "common";

const useStripeBilling = () => {
    const translations = useTranslations();
    const user = useUser();
    const { organizationId } = useParams();
    const [stripeLoading, setStripeLoading] = useState(false);
    const [loadingButton, setLoadingButton] = useState<number | null>(null);
    const currentUrl = window.location.origin + "#/dashboard/organizations";
    const cancelUrl = window.location.origin + `#/cancel`;
    const [rules, setRules] = useState<SubscriptionRule[]>([]);
    const [suggested, setSuggested] = useState<SubscriptionRule | null>(null);
    const [allSubscriptions, setAllSubscriptions] = useState<Subscription[]>([]);
    const [activeSubscription, setActiveSubscription] = useState<string | null>(null);
    const navigate = useNavigate();
    const [loading, setLoading] = useState<boolean>(false);
    const [billingInterval, setBillingInterval] = useState<"month" | "year">("month");
    const getPriceByInterval = (rule: SubscriptionRule, interval: "month" | "year" | "") => {
        if (rule.prices.length === 1 && rule.prices[0].interval === "") {
            return rule.prices[0];
        }

        return rule.prices.find((price) => price.interval === interval);
    };

    const handleSelectPlan = async (rule: SubscriptionRule) => {
        const selectedPrice = getPriceByInterval(rule, billingInterval);
        const providerId = selectedPrice?.provider_price_id || "";
        const userEmail = user?.email || "";
        setLoadingButton(rule.id);
        setStripeLoading(true);
        try {
            const response = await stripeApiTest.post(`stripe/subscription/checkout`,
                {
                    organization_id: organizationId,
                    email: userEmail,
                    name: rule.name,
                    price_id: providerId,
                    users_count: 0, //deprecated
                    cancel_url: cancelUrl,
                    success_url: currentUrl,
                    user_id: user?.userId,
                }
            );
            if (!response) throw new Error(`Errore ${response}`);

            const data = response.data;
            if (data.checkout_url) {
                window.location.href = data.checkout_url;
            } else {
                setStripeLoading(false);
                setLoadingButton(null);
                throw new Error("no checkout_url in response");
            }
        } catch (error) {
        }
    };

    const handleUpgradePlan = async (provider_price_id: string, subscriptionId: string) => {
        setStripeLoading(true);
        try {
            await stripeApiTest.post("stripe/subscription/update-plan",
                {
                    subscription_id: subscriptionId, //http://192.168.3.55:5236/billing/subscription/3901
                    new_price_id: provider_price_id, //rules[0].prices[0].provider_price_id, http://192.168.3.55:5236/billing/subscription-rules/3901
                    prorate: true,
                },
            );
            await new Promise(resolve => setTimeout(resolve, 2000)); // Attendi 2 secondi prima di caricare le subscription
            await loadActiveSubscriptions();
        } catch (error: any) {
        } finally {
            setStripeLoading(false);
        }
    };

    const formatPrice = (price?: Price) => {
        if (!price) return "Free";
        const interval = price.interval === "month" ? getTranslation(translations, "generic.month.caption") : price.interval === "year" ? getTranslation(translations, "generic.year.caption") : price.interval;
        return `${price.price}/${interval}`;
    };

    const isSuggestedPlan = (rule: SubscriptionRule) => rule.id === suggested?.id;

    const loadActiveSubscriptions = async () => {
        if (organizationId) {
            setLoading(true);
            try {
                const response = await stripeApiTest.get(`billing/subscription/${organizationId}`,
                );

                const res = response.data;
                if (res.subscriptions) {
                    const subscription = (res.subscriptions as Subscription[])
                        .find((s: Subscription) => s.active)?.current_price_id || null
                    setActiveSubscription(subscription);
                    setAllSubscriptions(res.subscriptions);
                } else {
                    setActiveSubscription(null);
                }
            } catch (err) {
            } finally {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        const fetchSubscriptionRules = async () => {
            setLoading(true);
            try {
                const _organizationId = organizationId
                    ? `billing/subscription-rules/${organizationId}`
                    : `billing/subscription-rules`;

                const response = await stripeApiTest.get(_organizationId);
                const res = response.data;

                setRules(res.rules);
                setSuggested(res.suggested);

                if (res.current_subscription && res.current_subscription.length > 0) {
                    const activeSub = res.current_subscription[0];
                    setActiveSubscription(activeSub.current_price_id || null);
                    setAllSubscriptions(res.current_subscription);
                } else {
                    setActiveSubscription(null);
                    setAllSubscriptions([]);
                }
            } catch (error) {
            } finally {
                setLoading(false);
            }
        };
        fetchSubscriptionRules();
    }, [organizationId, user?.userId]);

    const fetchSubscriptions = async () => {
        if (!user) return;

        setLoading(true);

        try {
            const response = await stripeApiTest.get(
                `/billing/subscriptions/admin/${user.userId}`,
            );
            setAllSubscriptions(response.data.subscriptions);
        } catch (err) {
        } finally {
            setLoading(false);
        }
    };

    return {
        handleSelectPlan,
        formatPrice,
        isSuggestedPlan,
        loading,
        navigate,
        rules,
        stripeLoading,
        loadingButton,
        activeSubscription,
        billingInterval,
        setBillingInterval,
        getPriceByInterval,
        handleUpgradePlan,
        allSubscriptions,
        fetchSubscriptions
    };
};

export default useStripeBilling;
